import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { extractHashtags } from '../../utils/hashtags';
import { API } from '../../utils/api';
import { ReactionBar, TimeOfDayBadge } from '../analytics/ReactionBar';
import SimilarUrlsPanel from './SimilarUrlsPanel';

const RESULTS_PER_PAGE = 30;

/**
 * SearchResultWindow - A closeable, paginated URL Collection Window.
 * Stores a filter snapshot when created. Has Apply/Revert buttons.
 * Apply: filters down URLs using current page controls (can only decrease).
 * Revert: restores page controls back to the snapshot state.
 */
const SearchResultWindow = ({
  id,
  title,
  searchType,
  results = [],
  filteredResults,
  onClose,
  onResultClick,
  token,
  showToast,
  accentColor = '#FDE047',
  onApplyFilters,
  onRevertFilters,
  onRestoreSearchSettings,
  filterSnapshot,
  isFiltered,
  maxResults = 600,
  plotOnMap = false,
  onTogglePlotOnMap
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [taggedIds, setTaggedIds] = useState(new Set());
  const [selectedCatFilter, setSelectedCatFilter] = useState(null);
  const [similarTarget, setSimilarTarget] = useState(null);

  const handleTagEducatedIndividual = useCallback((resultId) => {
    if (!resultId || taggedIds.has(resultId)) return;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/educated-individual/tag/${resultId}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function() {
      if (xhr.status === 200) {
        setTaggedIds(prev => new Set([...prev, resultId]));
        showToast && showToast('Tagged as Educated Individual Site!', 'success');
      } else {
        showToast && showToast('Could not tag this result', 'error');
      }
    };
    xhr.onerror = function() { showToast && showToast('Tag failed', 'error'); };
    xhr.send();
  }, [token, taggedIds, showToast]);

  const activeResults = filteredResults || results;

  // Extract relevant categories only - filter out unrelated ones
  // A category is relevant if it appears in ≥3% of results OR its name matches search query words
  const allCategories = useMemo(() => {
    const catMap = {};
    activeResults.forEach(r => {
      (r.categories || []).forEach(cat => {
        catMap[cat] = (catMap[cat] || 0) + 1;
      });
    });
    
    const totalCount = activeResults.length;
    const threshold = Math.max(2, Math.ceil(totalCount * 0.03)); // At least 3% or 2 results
    
    // Extract search query words for relevance matching (from window title)
    const queryWords = (title || '').toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 3); // Skip short words like "how", "to", "a"
    
    return Object.entries(catMap)
      .filter(([name, count]) => {
        // Keep if count meets threshold
        if (count >= threshold) return true;
        // Keep if category name has word overlap with the search query
        const catWords = name.toLowerCase().split(/[\s,\-_]+/).filter(w => w.length > 3);
        return catWords.some(cw => queryWords.some(qw => cw.includes(qw) || qw.includes(cw)));
      })
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
  }, [activeResults, title]);

  // First 30 words of search query for display
  const searchQueryPreview = useMemo(() => {
    if (!title) return '';
    const words = title.split(/\s+/).slice(0, 30);
    return words.join(' ') + (title.split(/\s+/).length > 30 ? '...' : '');
  }, [title]);

  const displayResults = useMemo(() => {
    let results = selectedCatFilter
      ? activeResults.filter(r => (r.categories || []).includes(selectedCatFilter))
      : activeResults;
    // Limit total displayed to maxResults setting
    return results.slice(0, maxResults);
  }, [activeResults, selectedCatFilter, maxResults]);

  const totalPages = Math.max(1, Math.ceil(displayResults.length / RESULTS_PER_PAGE));
  const paginatedResults = useMemo(() => {
    const start = (currentPage - 1) * RESULTS_PER_PAGE;
    return displayResults.slice(start, start + RESULTS_PER_PAGE);
  }, [displayResults, currentPage]);

  const handleCatClick = (catName) => {
    setSelectedCatFilter(prev => prev === catName ? null : catName);
    setCurrentPage(1);
  };

  const PageNav = () => {
    const getPageNumbers = () => {
      if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
      const pages = [1];
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
      return pages;
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, padding: '4px 0', flexWrap: 'wrap' }}>
        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
          style={{ background: currentPage === 1 ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255,255,255,0.1)', border: '1px solid rgba(203, 213, 225, 0.8)', color: currentPage === 1 ? '#444' : '#ccc', padding: '3px 8px', borderRadius: 5, cursor: currentPage === 1 ? 'default' : 'pointer', fontSize: '0.65rem', fontWeight: 600 }}
          data-testid={`page-prev-${id}`}>Prev</button>
        {getPageNumbers().map((page, i) => page === '...' ? (
          <span key={`e-${i}`} style={{ color: '#555', fontSize: '0.6rem', padding: '0 1px' }}>...</span>
        ) : (
          <button key={page} onClick={() => setCurrentPage(page)}
            style={{ background: page === currentPage ? accentColor : 'rgba(255, 255, 255, 0.85)', border: page === currentPage ? `1px solid ${accentColor}` : '1px solid rgba(255,255,255,0.06)', color: page === currentPage ? '#fff' : '#999', padding: '3px 7px', borderRadius: 5, cursor: 'pointer', fontSize: '0.65rem', fontWeight: page === currentPage ? 700 : 500, minWidth: 24, textAlign: 'center' }}
            data-testid={`page-${page}-${id}`}>{page}</button>
        ))}
        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
          style={{ background: currentPage === totalPages ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255,255,255,0.1)', border: '1px solid rgba(203, 213, 225, 0.8)', color: currentPage === totalPages ? '#444' : '#ccc', padding: '3px 8px', borderRadius: 5, cursor: currentPage === totalPages ? 'default' : 'pointer', fontSize: '0.65rem', fontWeight: 600 }}
          data-testid={`page-next-${id}`}>Next</button>
        <span style={{ fontSize: '0.55rem', color: '#555', marginLeft: 4 }}>{displayResults.length} URLs</span>
      </div>
    );
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(20, 10, 40, 0.98))',
      borderRadius: 10, border: `1px solid ${accentColor}30`, overflow: 'hidden', transition: 'all 0.2s'
    }} data-testid={`search-window-${id}`}>
      {/* Row 1: search type badge + title + count + close */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px',
        background: `linear-gradient(90deg, ${accentColor}18, transparent)`, borderBottom: `1px solid ${accentColor}10`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{
            background: `${accentColor}25`, color: accentColor, padding: '1px 6px', borderRadius: 8,
            fontSize: '0.58rem', fontWeight: 700, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.5px'
          }} data-testid={`search-type-badge-${id}`}>{searchType}</span>
          <span style={{ color: '#d4d4d8', fontSize: '0.72rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
          <span style={{ color: '#555', fontSize: '0.5rem', whiteSpace: 'nowrap' }}>
            ({activeResults.length}{isFiltered ? `/${results.length}` : ''} URLs)
          </span>
        </div>
        {/* Close */}
        <button onClick={() => onClose(id)} style={{
          background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.35)', color: '#f87171',
          width: 24, height: 24, borderRadius: 5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.85rem', fontWeight: 700, flexShrink: 0, transition: 'all 0.15s'
        }} data-testid={`close-window-${id}`} title="Remove this collection window">X</button>
      </div>
      {/* Row 2: control buttons */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', flexWrap: 'wrap',
        background: 'rgba(255,255,255,0.015)', borderBottom: `1px solid ${accentColor}12`
      }}>
        {/* Apply Filters button */}
        <button onClick={() => onApplyFilters && onApplyFilters(id)}
          style={{
            background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.35)', color: '#10b981',
            padding: '2px 7px', borderRadius: 5, cursor: 'pointer', fontSize: '0.52rem', fontWeight: 700,
            whiteSpace: 'nowrap', transition: 'all 0.15s'
          }}
          data-testid={`apply-filters-${id}`}
          title="Apply current search parameter controls to filter this window's URLs (can only decrease)">
          <i className="fas fa-filter" style={{ marginRight: 3, fontSize: '0.45rem' }} />Apply
        </button>
        {/* Revert Filters button */}
        <button onClick={() => onRevertFilters && onRevertFilters(id)}
          style={{
            background: isFiltered ? 'rgba(245,158,11,0.15)' : 'rgba(255, 255, 255, 0.85)',
            border: isFiltered ? '1px solid rgba(245,158,11,0.35)' : '1px solid rgba(203, 213, 225, 0.8)',
            color: isFiltered ? '#f59e0b' : '#555',
            padding: '2px 7px', borderRadius: 5, cursor: isFiltered ? 'pointer' : 'default',
            fontSize: '0.52rem', fontWeight: 700, whiteSpace: 'nowrap', transition: 'all 0.15s'
          }}
          data-testid={`revert-filters-${id}`}
          title="Revert search parameters to the state when this window was opened (or last set)">
          <i className="fas fa-undo" style={{ marginRight: 3, fontSize: '0.45rem' }} />Revert
        </button>
        {/* Restore Search Settings button */}
        {filterSnapshot && (
          <button onClick={() => onRestoreSearchSettings && onRestoreSearchSettings(id)}
            style={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11,0.18), rgba(253, 224, 71,0.18))',
              border: '1px solid rgba(245, 158, 11,0.45)',
              color: '#60a5fa',
              padding: '2px 7px', borderRadius: 5, cursor: 'pointer',
              fontSize: '0.52rem', fontWeight: 700, whiteSpace: 'nowrap', transition: 'all 0.15s'
            }}
            data-testid={`restore-settings-${id}`}
            title="Restore the main search page controls to exactly the settings used when this window was created">
            <i className="fas fa-sync-alt" style={{ marginRight: 3, fontSize: '0.45rem' }} />Auto-Update
          </button>
        )}
        {/* Plot on Map checkbox */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer',
          color: plotOnMap ? '#FEF08A' : '#71717a', fontSize: '0.52rem', fontWeight: 600,
          whiteSpace: 'nowrap', transition: 'all 0.15s'
        }} data-testid={`plot-on-map-checkbox-${id}`} title="Toggle: plot all URLs from this window on the map">
          <input type="checkbox" checked={plotOnMap}
            onChange={() => onTogglePlotOnMap && onTogglePlotOnMap(id)}
            style={{ accentColor: '#FEF08A', width: 12, height: 12 }} />
          Plot Map
        </label>
        {/* Show on Map button */}
        <button onClick={async () => {
            // First try to extract locations for URLs missing them
            try {
              const urls = activeResults.map(r => r.url).filter(Boolean);
              const xhr = new XMLHttpRequest();
              xhr.open('POST', `${API}/map/extract-window-locations`, false); // synchronous for simplicity
              xhr.setRequestHeader('Authorization', `Bearer ${token}`);
              xhr.setRequestHeader('Content-Type', 'application/json');
              xhr.send(JSON.stringify({ urls }));
              
              if (xhr.status === 200) {
                const resp = JSON.parse(xhr.responseText);
                const locMap = resp.locations || {};
                // Enrich results with extracted locations
                const enrichedResults = activeResults.map(r => {
                  const loc = locMap[r.url];
                  if (loc) {
                    return {
                      ...r,
                      latitude: loc.latitude || r.latitude,
                      longitude: loc.longitude || r.longitude,
                      extracted_locations: loc.extracted_locations || r.extracted_locations
                    };
                  }
                  return r;
                });
                const withLoc = enrichedResults.filter(r => r.latitude && r.longitude);
                const locationData = enrichedResults.map(r => ({
                  title: r.title, url: r.url, latitude: r.latitude, longitude: r.longitude,
                  article_type: r.article_type, categories: r.categories,
                  extracted_locations: r.extracted_locations
                }));
                const mapEvent = new CustomEvent('showWindowOnMap', { detail: { windowId: id, title, results: locationData } });
                window.dispatchEvent(mapEvent);
                showToast && showToast(`Showing ${withLoc.length}/${activeResults.length} URLs on map (${resp.extracted || 0} newly extracted)`, 'success');
                return;
              }
            } catch (e) {
              // Fallback to original behavior
            }
            
            const locationData = activeResults.map(r => ({
              title: r.title, url: r.url, latitude: r.latitude, longitude: r.longitude,
              article_type: r.article_type, categories: r.categories
            }));
            const mapEvent = new CustomEvent('showWindowOnMap', { detail: { windowId: id, title, results: locationData } });
            window.dispatchEvent(mapEvent);
            showToast && showToast(`Showing ${activeResults.length} URLs on map`, 'success');
          }}
            style={{
              background: 'rgba(244,114,182,0.15)', border: '1px solid rgba(244,114,182,0.35)', color: '#047857',
              padding: '2px 7px', borderRadius: 5, cursor: 'pointer',
              fontSize: '0.52rem', fontWeight: 700, whiteSpace: 'nowrap', transition: 'all 0.15s'
            }}
            data-testid={`show-on-map-${id}`}
            title="Show this window's URLs on the map">
            <i className="fas fa-map-marker-alt" style={{ marginRight: 3, fontSize: '0.45rem' }} />Map
          </button>
        </div>

      {/* Filter status bar */}
      {isFiltered && (
        <div style={{
          padding: '3px 10px', background: 'rgba(16,185,129,0.06)', borderBottom: '1px solid rgba(16,185,129,0.1)',
          display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.48rem', color: '#10b981'
        }}>
          <i className="fas fa-filter" style={{ fontSize: '0.42rem' }} />
          <span>Filtered: showing {activeResults.length} of {results.length} original URLs</span>
          <button onClick={() => onRevertFilters && onRevertFilters(id)}
            style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', fontSize: '0.48rem', textDecoration: 'underline', padding: 0 }}>
            Revert to original
          </button>
        </div>
      )}

      {/* Search query preview */}
      {searchQueryPreview && (
        <div style={{
          padding: '2px 10px', color: '#4a4a5a', fontSize: '0.42rem', lineHeight: 1.3,
          borderBottom: '1px solid rgba(255, 255, 255, 0.85)', fontStyle: 'italic',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }} data-testid={`search-query-preview-${id}`} title={title}>
          <i className="fas fa-search" style={{ marginRight: 3, fontSize: '0.38rem', opacity: 0.5 }} />
          {searchQueryPreview}
        </div>
      )}

      {/* Category cluster bar */}
      {allCategories.length > 0 && (
        <div style={{
          padding: '4px 8px', display: 'flex', flexWrap: 'wrap', gap: 3,
          borderBottom: '1px solid rgba(255, 255, 255, 0.85)', background: 'rgba(255,255,255,0.015)'
        }}>
          <span style={{ color: '#555', fontSize: '0.48rem', alignSelf: 'center', marginRight: 2 }}>FILTER BY:</span>
          {allCategories.map(({ name, count }) => (
            <button key={name} onClick={() => handleCatClick(name)}
              style={{
                background: selectedCatFilter === name ? 'rgba(253, 230, 138,0.25)' : 'rgba(253, 230, 138,0.08)',
                border: `1px solid ${selectedCatFilter === name ? 'rgba(253, 230, 138,0.5)' : 'rgba(253, 230, 138,0.15)'}`,
                color: selectedCatFilter === name ? '#FEF08A' : '#d4a0c0', padding: '1px 5px', borderRadius: 6,
                fontSize: '0.52rem', cursor: 'pointer', fontWeight: selectedCatFilter === name ? 700 : 500,
                whiteSpace: 'nowrap', transition: 'all 0.15s'
              }}
              data-testid={`cat-filter-${name.substring(0,10)}-${id}`}
              title={`${name} (${count} URLs) - Click to filter`}
            >{name.substring(0, 17)}{name.length > 17 ? '..' : ''} <span style={{ opacity: 0.6 }}>{count}</span></button>
          ))}
          {selectedCatFilter && (
            <button onClick={() => { setSelectedCatFilter(null); setCurrentPage(1); }}
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', padding: '1px 5px', borderRadius: 6, fontSize: '0.5rem', cursor: 'pointer', fontWeight: 600 }}
              data-testid={`clear-cat-filter-${id}`}>Clear</button>
          )}
        </div>
      )}

      {/* Microfont instructions */}
      <div style={{ padding: '2px 8px', color: '#444', fontSize: '0.45rem', lineHeight: 1.3, borderBottom: '1px solid rgba(255, 255, 255, 0.85)' }}>
        Click any category tag above to filter. Click a URL title to open. Use Apply to filter with current controls. Revert restores original search state.
      </div>

      {totalPages > 1 && <PageNav />}

      {/* Ultra-thin scrollable result rows */}
      <div style={{ maxHeight: 350, overflowY: 'auto', padding: '2px 6px' }}>
        {paginatedResults.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#555', fontSize: '0.7rem' }}>
            {selectedCatFilter ? `No results in "${selectedCatFilter}". Try clearing the filter.` : isFiltered ? 'All URLs filtered out. Click Revert to restore.' : 'No results found.'}
          </div>
        ) : (
          paginatedResults.map((result, idx) => {
            const hashtags = extractHashtags(result.title, result.snippet, result.article_type);
            return (
              <div key={result.id || result._id || `r-${idx}`} style={{
                padding: '3px 4px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)',
                display: 'flex', flexDirection: 'column', gap: 1
              }} data-testid={`result-row-${id}-${idx}`}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                  <span style={{ color: '#555', fontSize: '0.5rem', minWidth: 18, paddingTop: 1, textAlign: 'right' }}>
                    {(currentPage - 1) * RESULTS_PER_PAGE + idx + 1}.
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <a href={result.url} target="_blank" rel="noopener noreferrer"
                      style={{ color: '#93c5fd', fontSize: '0.68rem', fontWeight: 500, textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={`Open: ${result.title}`}
                    >{result.title || 'Untitled'}</a>
                    {result.snippet && (
                      <p style={{ color: '#777', fontSize: '0.55rem', margin: '1px 0 0', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3 }}>{result.snippet}</p>
                    )}
                  </div>
                  {result.quality_score > 0 && (
                    <span style={{ background: result.quality_score >= 65 ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)', color: result.quality_score >= 65 ? '#34d399' : '#fbbf24', fontSize: '0.45rem', padding: '0px 3px', borderRadius: 3, flexShrink: 0 }}>Q{Math.round(result.quality_score)}</span>
                  )}
                </div>
                {/* Inline tags */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, paddingLeft: 22, alignItems: 'center' }}>
                  {result.article_type && (
                    <span style={{ background: 'rgba(253, 224, 71,0.12)', color: '#047857', padding: '0px 4px', borderRadius: 6, fontSize: '0.48rem' }}>{result.article_type}</span>
                  )}
                  {result.categories?.map((cat, ci) => (
                    <span key={ci} style={{ background: 'rgba(253, 230, 138,0.1)', color: '#e879a0', padding: '0px 4px', borderRadius: 6, fontSize: '0.48rem', cursor: 'pointer' }}
                      onClick={() => handleCatClick(cat)} title={cat}>{cat.substring(0, 17)}{cat.length > 17 ? '..' : ''}</span>
                  ))}
                  {hashtags.slice(0, 3).map((tag, ti) => (
                    <span key={ti} style={{ background: 'rgba(16,185,129,0.08)', color: '#6ee7b7', padding: '0px 4px', borderRadius: 6, fontSize: '0.48rem' }}>{tag}</span>
                  ))}
                  {result.id && (
                    <button onClick={(e) => { e.stopPropagation(); handleTagEducatedIndividual(result.id); }}
                      disabled={taggedIds.has(result.id)}
                      style={{ background: taggedIds.has(result.id) ? 'rgba(252, 211, 77,0.25)' : 'rgba(252, 211, 77,0.08)', border: '1px solid rgba(252, 211, 77,0.2)', color: taggedIds.has(result.id) ? '#047857' : '#a855f7', padding: '0px 4px', borderRadius: 6, fontSize: '0.45rem', cursor: taggedIds.has(result.id) ? 'default' : 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                      data-testid={`tag-ei-${result.id}`} title="Mark as Educated Individual Site"
                    >{taggedIds.has(result.id) ? 'EI' : 'Tag EI'}</button>
                  )}
                  {result.id && (
                    <button onClick={(e) => { e.stopPropagation(); setSimilarTarget({ id: result.id, title: result.title }); }}
                      style={{ background: 'rgba(245, 158, 11,0.08)', border: '1px solid rgba(245, 158, 11,0.2)', color: '#60a5fa', padding: '0px 4px', borderRadius: 6, fontSize: '0.45rem', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                      data-testid={`find-similar-${result.id}`} title="Find semantically similar URLs">
                      Similar
                    </button>
                  )}
                  {result.last_time_of_day && <TimeOfDayBadge timeOfDay={result.last_time_of_day} accessCounts={result.time_access_counts} />}
                </div>
                {result.id && (
                  <div style={{ paddingLeft: 22, marginTop: 1 }}>
                    <ReactionBar resultId={result.id} existingReactions={result.reactions} compact={true} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 && <PageNav />}
      
      <div style={{ padding: '2px 8px 4px', color: '#3a3a3a', fontSize: '0.42rem', textAlign: 'center' }}>
        URL Collection Window &middot; {activeResults.length}{isFiltered ? `/${results.length}` : ''} URLs &middot; Apply=filter down &middot; Revert=restore original controls
      </div>
      
      {/* Similar URLs Panel */}
      {similarTarget && (
        <SimilarUrlsPanel
          resultId={similarTarget.id}
          resultTitle={similarTarget.title}
          onClose={() => setSimilarTarget(null)}
        />
      )}
    </div>
  );
};

/**
 * ComparisonView - Side-by-side view of two URL Collection Windows.
 * Highlights shared URLs with a distinctive badge and allows bulk export.
 */
const ComparisonView = ({ data, onExport, onClose, onResultClick, token, showToast }) => {
  const { winA, winB, shared, sharedSet, onlyA, onlyB } = data;
  const [viewMode, setViewMode] = useState('shared'); // 'shared' | 'side-by-side'

  const SharedBadge = () => (
    <span style={{
      background: 'linear-gradient(90deg, rgba(16,185,129,0.25), rgba(245, 158, 11,0.25))',
      border: '1px solid rgba(16,185,129,0.5)', color: '#34d399',
      padding: '0 5px', borderRadius: 8, fontSize: '0.48rem', fontWeight: 700,
      letterSpacing: '0.5px', animation: 'pulse 2s infinite'
    }} data-testid="shared-url-badge">SHARED</span>
  );

  const renderResultRow = (result, idx, isShared) => (
    <div key={result.url || idx} style={{
      padding: '4px 6px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)',
      background: isShared ? 'rgba(16,185,129,0.04)' : 'transparent',
      display: 'flex', flexDirection: 'column', gap: 1
    }} data-testid={`compare-result-${idx}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ color: '#555', fontSize: '0.5rem', minWidth: 18, textAlign: 'right' }}>{idx + 1}.</span>
        <a href={result.url} target="_blank" rel="noopener noreferrer"
          style={{ color: isShared ? '#34d399' : '#93c5fd', fontSize: '0.66rem', fontWeight: 500, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {result.title || 'Untitled'}
        </a>
        {isShared && <SharedBadge />}
      </div>
      {result.snippet && (
        <p style={{ color: '#666', fontSize: '0.52rem', margin: '1px 0 0 22px', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{result.snippet}</p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, paddingLeft: 22 }}>
        {result.article_type && <span style={{ background: 'rgba(253, 224, 71,0.12)', color: '#047857', padding: '0px 4px', borderRadius: 6, fontSize: '0.46rem' }}>{result.article_type}</span>}
        {result.categories?.slice(0, 3).map((cat, ci) => (
          <span key={ci} style={{ background: 'rgba(253, 230, 138,0.1)', color: '#e879a0', padding: '0px 4px', borderRadius: 6, fontSize: '0.46rem' }}>{cat.substring(0, 17)}</span>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(20, 15, 45, 0.98), rgba(10, 5, 30, 0.98))',
      borderRadius: 14, border: '2px solid rgba(245, 158, 11,0.35)',
      marginBottom: 12, overflow: 'hidden',
      boxShadow: '0 8px 30px rgba(245, 158, 11,0.15)'
    }} data-testid="comparison-view">
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '8px 14px', background: 'linear-gradient(90deg, rgba(245, 158, 11,0.12), rgba(16,185,129,0.08))',
        borderBottom: '1px solid rgba(245, 158, 11,0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fas fa-columns" style={{ color: '#60a5fa', fontSize: '0.8rem' }} />
          <span style={{ color: '#334155', fontSize: '0.8rem', fontWeight: 700 }}>Window Comparison</span>
          <span style={{ color: '#555', fontSize: '0.55rem' }}>
            "{winA.title}" vs "{winB.title}"
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* View mode toggle */}
          <div style={{ display: 'flex', gap: 2, background: 'rgba(255, 255, 255, 0.85)', borderRadius: 6, padding: 2 }}>
            <button onClick={() => setViewMode('shared')}
              style={{ background: viewMode === 'shared' ? 'rgba(16,185,129,0.2)' : 'transparent', color: viewMode === 'shared' ? '#34d399' : '#71717a', border: 'none', padding: '3px 8px', borderRadius: 4, fontSize: '0.55rem', fontWeight: 600, cursor: 'pointer' }}
              data-testid="compare-view-shared">Shared URLs</button>
            <button onClick={() => setViewMode('side-by-side')}
              style={{ background: viewMode === 'side-by-side' ? 'rgba(245, 158, 11,0.2)' : 'transparent', color: viewMode === 'side-by-side' ? '#60a5fa' : '#71717a', border: 'none', padding: '3px 8px', borderRadius: 4, fontSize: '0.55rem', fontWeight: 600, cursor: 'pointer' }}
              data-testid="compare-view-sidebyside">Side-by-Side</button>
          </div>
          {shared.length > 0 && (
            <button onClick={onExport}
              style={{
                background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)',
                color: '#10b981', padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
                fontSize: '0.55rem', fontWeight: 700
              }}
              data-testid="export-shared-btn">
              <i className="fas fa-download" style={{ marginRight: 3 }} />Export Shared ({shared.length})
            </button>
          )}
          <button onClick={onClose}
            style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', width: 24, height: 24, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}
            data-testid="close-comparison">X</button>
        </div>
      </div>

      {/* Stats bar */}
      <div style={{
        display: 'flex', gap: 12, padding: '6px 14px',
        background: 'rgba(0,0,0,0.2)', borderBottom: '1px solid rgba(255, 255, 255, 0.85)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: winA.accentColor || '#FDE047' }} />
          <span style={{ color: '#a1a1aa', fontSize: '0.52rem' }}>{winA.title}: <strong style={{ color: '#334155' }}>{(winA.results || []).length}</strong> URLs</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: winB.accentColor || '#3b82f6' }} />
          <span style={{ color: '#a1a1aa', fontSize: '0.52rem' }}>{winB.title}: <strong style={{ color: '#334155' }}>{(winB.results || []).length}</strong> URLs</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <SharedBadge />
          <span style={{ color: '#a1a1aa', fontSize: '0.52rem' }}>Shared: <strong style={{ color: '#34d399' }}>{shared.length}</strong> URLs</span>
        </div>
        <span style={{ color: '#555', fontSize: '0.48rem', marginLeft: 'auto' }}>
          Only in A: {onlyA.length} | Only in B: {onlyB.length} | Overlap: {shared.length > 0 ? ((shared.length / Math.min((winA.results || []).length, (winB.results || []).length)) * 100).toFixed(1) : 0}%
        </span>
      </div>

      {/* Content */}
      {viewMode === 'shared' ? (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {shared.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#555' }}>
              <i className="fas fa-unlink" style={{ fontSize: '1.5rem', marginBottom: 8, display: 'block', opacity: 0.4 }} />
              <p style={{ fontSize: '0.75rem' }}>No shared URLs between these two windows.</p>
              <p style={{ fontSize: '0.55rem', color: '#444' }}>Try comparing windows with overlapping search terms.</p>
            </div>
          ) : (
            shared.map((r, i) => renderResultRow(r, i, true))
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
          {/* Window A */}
          <div style={{ borderRight: '1px solid rgba(255,255,255,0.06)', maxHeight: 400, overflowY: 'auto' }}>
            <div style={{ padding: '4px 8px', background: `${winA.accentColor || '#FDE047'}12`, borderBottom: '1px solid rgba(255, 255, 255, 0.85)' }}>
              <span style={{ color: winA.accentColor || '#FDE047', fontSize: '0.6rem', fontWeight: 700 }}>{winA.title}</span>
              <span style={{ color: '#555', fontSize: '0.48rem', marginLeft: 6 }}>{(winA.results || []).length} URLs</span>
            </div>
            {(winA.results || []).map((r, i) => renderResultRow(r, i, sharedSet.has(r.url)))}
          </div>
          {/* Window B */}
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <div style={{ padding: '4px 8px', background: `${winB.accentColor || '#3b82f6'}12`, borderBottom: '1px solid rgba(255, 255, 255, 0.85)' }}>
              <span style={{ color: winB.accentColor || '#3b82f6', fontSize: '0.6rem', fontWeight: 700 }}>{winB.title}</span>
              <span style={{ color: '#555', fontSize: '0.48rem', marginLeft: 6 }}>{(winB.results || []).length} URLs</span>
            </div>
            {(winB.results || []).map((r, i) => renderResultRow(r, i, sharedSet.has(r.url)))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * SearchWindowManager - Manages URL Collection Windows with filter snapshots.
 * Each window stores the filter state at creation time.
 * Apply: uses current page controls to filter window URLs (only decreases).
 * Revert: restores page controls to the snapshot captured at window creation.
 */
const SearchWindowManager = ({ windows, onCloseWindow, onResultClick, token, showToast, activeFilters, autoUpdateEnabled, setAutoUpdateEnabled, onRevertToSnapshot, maxUrlsPerWindow = 600, onChangeMaxUrls, miniatureAnchorRef }) => {
  // Track per-window filter snapshots and filtered results
  const [windowSnapshots, setWindowSnapshots] = useState({});
  const [windowFilteredResults, setWindowFilteredResults] = useState({});
  const prevWindowIds = useRef(new Set());
  const [localMax, setLocalMax] = useState(maxUrlsPerWindow);
  
  // Comparison mode state
  const [compareMode, setCompareMode] = useState(false);
  const [compareWindowIds, setCompareWindowIds] = useState([]);
  
  // Plot on Map state — tracks which windows have their checkbox checked
  const [plottedWindowIds, setPlottedWindowIds] = useState(new Set());
  
  // Toggle plot on map for a window
  const togglePlotOnMap = useCallback((windowId) => {
    setPlottedWindowIds(prev => {
      const next = new Set(prev);
      if (next.has(windowId)) {
        next.delete(windowId);
        // Dispatch event to remove window from map
        const mapEvent = new CustomEvent('removeWindowFromMap', { detail: { windowId } });
        window.dispatchEvent(mapEvent);
      } else {
        next.add(windowId);
        // Find the window and extract location data
        const win = windows.find(w => w.id === windowId);
        if (win) {
          const locationData = (win.results || []).map(r => ({
            title: r.title, url: r.url, latitude: r.latitude, longitude: r.longitude,
            article_type: r.article_type, categories: r.categories,
            extracted_locations: r.extracted_locations
          }));
          const mapEvent = new CustomEvent('plotWindowOnMap', { 
            detail: { windowId, title: win.title, results: locationData } 
          });
          window.dispatchEvent(mapEvent);
        }
      }
      return next;
    });
  }, [windows]);

  // Capture snapshot when a new window appears & auto-plot on map
  useEffect(() => {
    if (!windows || !activeFilters) return;
    const currentIds = new Set(windows.map(w => w.id));
    windows.forEach(win => {
      if (!prevWindowIds.current.has(win.id) && !windowSnapshots[win.id]) {
        // New window — snapshot current filters
        setWindowSnapshots(prev => ({
          ...prev,
          [win.id]: JSON.parse(JSON.stringify(activeFilters))
        }));
        // Auto-plot new window on map
        if (!plottedWindowIds.has(win.id)) {
          setPlottedWindowIds(prev => {
            const next = new Set(prev);
            next.add(win.id);
            return next;
          });
          const locationData = (win.results || []).map(r => ({
            title: r.title, url: r.url, latitude: r.latitude, longitude: r.longitude,
            article_type: r.article_type, categories: r.categories,
            extracted_locations: r.extracted_locations
          }));
          const mapEvent = new CustomEvent('plotWindowOnMap', {
            detail: { windowId: win.id, title: win.title, results: locationData }
          });
          window.dispatchEvent(mapEvent);
        }
      }
    });
    prevWindowIds.current = currentIds;
  }, [windows, activeFilters, windowSnapshots, plottedWindowIds]);

  // Clean up snapshots for closed windows
  useEffect(() => {
    if (!windows) return;
    const currentIds = new Set(windows.map(w => w.id));
    setWindowSnapshots(prev => {
      const next = {};
      for (const id of Object.keys(prev)) {
        if (currentIds.has(id)) next[id] = prev[id];
      }
      return next;
    });
    setWindowFilteredResults(prev => {
      const next = {};
      for (const id of Object.keys(prev)) {
        if (currentIds.has(id)) next[id] = prev[id];
      }
      return next;
    });
    // Clean up plotted window IDs for closed windows
    setPlottedWindowIds(prev => {
      const next = new Set();
      for (const id of prev) {
        if (currentIds.has(id)) {
          next.add(id);
        } else {
          // Dispatch removal event for closed window
          const mapEvent = new CustomEvent('removeWindowFromMap', { detail: { windowId: id } });
          window.dispatchEvent(mapEvent);
        }
      }
      return next;
    });
  }, [windows]);

  // Apply current filters to a specific window
  const applyFiltersToWindow = useCallback((windowId) => {
    const win = windows.find(w => w.id === windowId);
    if (!win || !activeFilters) return;

    let filtered = [...(win.results || [])];
    
    // Filter by selected doc types
    if (activeFilters.selectedDocTypes && activeFilters.selectedDocTypes.length > 0 && activeFilters.selectedDocTypes.length < 20) {
      filtered = filtered.filter(r => {
        if (!r.article_type) return true;
        return activeFilters.selectedDocTypes.includes(r.article_type);
      });
    }
    
    // Filter by certification keywords
    if (activeFilters.certificationFilters) {
      const certKeywordMap = {
        pearsonvue: ['pearsonvue', 'pearson vue', 'certification exam', 'prometric', 'comptia', 'cisco certified'],
        government: ['government', '.gov', 'federal', 'state agency', 'municipal', 'public sector'],
        advancedDegree: ['ph.d', 'phd', 'doctorate', 'master of science', 'master of arts', 'dissertation', 'thesis']
      };
      const activeCertKeys = Object.entries(activeFilters.certificationFilters).filter(([, v]) => v).map(([k]) => k);
      if (activeCertKeys.length > 0 && activeCertKeys.length < 3) {
        filtered = filtered.filter(r => {
          const text = `${r.title || ''} ${r.snippet || ''} ${r.url || ''}`.toLowerCase();
          return activeCertKeys.some(key => (certKeywordMap[key] || []).some(kw => text.includes(kw)));
        });
      }
    }
    
    // Filter by EI topic keywords
    if (activeFilters.educatedIndividualFilters) {
      const eiKeywords = {
        science: ['chemistry', 'physics', 'biology', 'chemical', 'molecule', 'atom', 'reaction', 'experiment', 'laboratory', 'scientific'],
        engineering: ['engineering', 'electrical', 'mechanical', 'civil', 'structural', 'circuit', 'voltage', 'materials'],
        construction: ['framing', 'drywall', 'plumbing', 'hvac', 'roofing', 'foundation', 'concrete', 'lumber'],
        culinary: ['cooking', 'recipe', 'stock', 'sauce', 'baking', 'roast', 'braise'],
        trades: ['welding', 'carpentry', 'masonry', 'plumber', 'electrician', 'apprentice', 'journeyman'],
        mathematics: ['calculus', 'algebra', 'geometry', 'trigonometry', 'equation', 'formula', 'theorem'],
        technology: ['programming', 'coding', 'software', 'hardware', 'network', 'database', 'algorithm']
      };
      const activeTopics = Object.entries(activeFilters.educatedIndividualFilters)
        .filter(([k, v]) => v && k !== 'favor_visuals' && k !== 'favor_pictures').map(([k]) => k);
      const totalTopics = Object.keys(eiKeywords).length;
      if (activeTopics.length > 0 && activeTopics.length < totalTopics) {
        filtered = filtered.filter(r => {
          const text = `${r.title || ''} ${r.snippet || ''} ${r.content || ''}`.toLowerCase();
          return activeTopics.some(topic => (eiKeywords[topic] || []).some(kw => text.includes(kw)));
        });
      }
    }
    
    // Filter by min word count
    if (activeFilters.minWordCount && activeFilters.minWordCount > 0) {
      filtered = filtered.filter(r => {
        const wc = r.word_count || r.content_length || 0;
        if (wc > 0) return wc >= activeFilters.minWordCount;
        return true;
      });
    }
    
    // Filter by aggregation mode with selected categories
    // selectedCategories contains IDs, but r.categories contains names - resolve IDs to names
    if (activeFilters.selectedCategories && activeFilters.selectedCategories.length > 0 && activeFilters.aggregation) {
      // Build a set of selected category names from IDs
      const selectedCatNames = (activeFilters.categories || [])
        .filter(c => activeFilters.selectedCategories.includes(c.id || c._id))
        .map(c => c.name);
      
      if (selectedCatNames.length > 0) {
        filtered = filtered.filter(r => {
          if (!r.categories || r.categories.length === 0) return false;
          if (activeFilters.aggregation === 'and') {
            return selectedCatNames.every(catName => r.categories.includes(catName));
          }
          return selectedCatNames.some(catName => r.categories.includes(catName));
        });
      }
    }
    
    // Filter by search query
    if (activeFilters.searchQuery && activeFilters.searchQuery.trim()) {
      const q = activeFilters.searchQuery.toLowerCase();
      filtered = filtered.filter(r => {
        const text = `${r.title || ''} ${r.snippet || ''} ${r.url || ''}`.toLowerCase();
        return text.includes(q);
      });
    }
    
    // URLs can only decrease — apply filtered results
    setWindowFilteredResults(prev => ({ ...prev, [windowId]: filtered }));
    
    // Update the snapshot to current filters (so next Revert goes back to these)
    setWindowSnapshots(prev => ({
      ...prev,
      [windowId]: JSON.parse(JSON.stringify(activeFilters))
    }));
    
    const removed = (win.results || []).length - filtered.length;
    showToast && showToast(`Filters applied: ${filtered.length} URLs remaining (${removed} filtered out)`, 'success');
  }, [windows, activeFilters, showToast]);

  // Revert page controls to the window's snapshot
  const revertToSnapshot = useCallback((windowId) => {
    const snapshot = windowSnapshots[windowId];
    if (!snapshot) {
      showToast && showToast('No saved filter state to revert to', 'info');
      return;
    }
    
    // Clear the filtered results for this window
    setWindowFilteredResults(prev => {
      const next = { ...prev };
      delete next[windowId];
      return next;
    });
    
    // Call parent to restore filter controls
    if (onRevertToSnapshot) {
      onRevertToSnapshot(snapshot);
      showToast && showToast('Search parameters reverted to original state', 'success');
    }
  }, [windowSnapshots, onRevertToSnapshot, showToast]);

  // Restore main search settings to the snapshot for a specific window
  const restoreSearchSettings = useCallback((windowId) => {
    const snapshot = windowSnapshots[windowId];
    if (!snapshot) {
      showToast && showToast('No saved search settings for this window', 'info');
      return;
    }
    if (onRevertToSnapshot) {
      onRevertToSnapshot(snapshot);
      showToast && showToast('Main search settings restored from this window', 'success');
    }
  }, [windowSnapshots, onRevertToSnapshot, showToast]);

  // Auto-update: apply filters to all windows when enabled and filters change
  const applyFiltersGlobal = useCallback((results) => {
    if (!activeFilters || !autoUpdateEnabled) return results;
    let filtered = [...results];
    
    if (activeFilters.selectedDocTypes && activeFilters.selectedDocTypes.length > 0 && activeFilters.selectedDocTypes.length < 20) {
      filtered = filtered.filter(r => !r.article_type || activeFilters.selectedDocTypes.includes(r.article_type));
    }
    if (activeFilters.certificationFilters) {
      const certKeywordMap = {
        pearsonvue: ['pearsonvue', 'pearson vue', 'certification exam', 'prometric', 'comptia', 'cisco certified'],
        government: ['government', '.gov', 'federal', 'state agency', 'municipal', 'public sector'],
        advancedDegree: ['ph.d', 'phd', 'doctorate', 'master of science', 'master of arts', 'dissertation', 'thesis']
      };
      const activeCertKeys = Object.entries(activeFilters.certificationFilters).filter(([, v]) => v).map(([k]) => k);
      if (activeCertKeys.length > 0 && activeCertKeys.length < 3) {
        filtered = filtered.filter(r => {
          const text = `${r.title || ''} ${r.snippet || ''} ${r.url || ''}`.toLowerCase();
          return activeCertKeys.some(key => (certKeywordMap[key] || []).some(kw => text.includes(kw)));
        });
      }
    }
    if (activeFilters.educatedIndividualFilters) {
      const eiKeywords = {
        science: ['chemistry', 'physics', 'biology', 'chemical', 'molecule', 'atom', 'reaction'],
        engineering: ['engineering', 'electrical', 'mechanical', 'civil', 'structural'],
        construction: ['framing', 'drywall', 'plumbing', 'hvac', 'roofing', 'foundation'],
        culinary: ['cooking', 'recipe', 'stock', 'sauce', 'baking'],
        trades: ['welding', 'carpentry', 'masonry', 'plumber', 'electrician'],
        mathematics: ['calculus', 'algebra', 'geometry', 'trigonometry', 'equation'],
        technology: ['programming', 'coding', 'software', 'hardware', 'network']
      };
      const activeTopics = Object.entries(activeFilters.educatedIndividualFilters)
        .filter(([k, v]) => v && k !== 'favor_visuals' && k !== 'favor_pictures').map(([k]) => k);
      if (activeTopics.length > 0 && activeTopics.length < 7) {
        filtered = filtered.filter(r => {
          const text = `${r.title || ''} ${r.snippet || ''} ${r.content || ''}`.toLowerCase();
          return activeTopics.some(topic => (eiKeywords[topic] || []).some(kw => text.includes(kw)));
        });
      }
    }
    if (activeFilters.minWordCount > 0) {
      filtered = filtered.filter(r => { const wc = r.word_count || r.content_length || 0; return wc === 0 || wc >= activeFilters.minWordCount; });
    }
    if (activeFilters.searchQuery && activeFilters.searchQuery.trim()) {
      const q = activeFilters.searchQuery.toLowerCase();
      filtered = filtered.filter(r => `${r.title || ''} ${r.snippet || ''} ${r.url || ''}`.toLowerCase().includes(q));
    }
    return filtered;
  }, [activeFilters, autoUpdateEnabled]);

  // Toggle window for comparison
  const toggleCompareWindow = useCallback((windowId) => {
    setCompareWindowIds(prev => {
      if (prev.includes(windowId)) return prev.filter(id => id !== windowId);
      if (prev.length >= 2) return [prev[1], windowId]; // Replace oldest
      return [...prev, windowId];
    });
  }, []);

  // Compute shared URLs between two compared windows
  const comparisonData = useMemo(() => {
    if (compareWindowIds.length !== 2 || !windows) return null;
    const winA = windows.find(w => w.id === compareWindowIds[0]);
    const winB = windows.find(w => w.id === compareWindowIds[1]);
    if (!winA || !winB) return null;

    const urlsA = new Set((winA.results || []).map(r => r.url));
    const urlsB = new Set((winB.results || []).map(r => r.url));
    const shared = [...urlsA].filter(url => urlsB.has(url));
    const sharedSet = new Set(shared);
    const onlyA = (winA.results || []).filter(r => !sharedSet.has(r.url));
    const onlyB = (winB.results || []).filter(r => !sharedSet.has(r.url));
    const sharedResults = (winA.results || []).filter(r => sharedSet.has(r.url));

    return { winA, winB, shared: sharedResults, sharedSet, onlyA, onlyB };
  }, [compareWindowIds, windows]);

  // Export shared URLs as CSV
  const exportSharedUrls = useCallback(() => {
    if (!comparisonData || comparisonData.shared.length === 0) {
      showToast && showToast('No shared URLs to export', 'info');
      return;
    }
    const rows = ['Title,URL,Categories,Article Type'];
    comparisonData.shared.forEach(r => {
      rows.push(`"${(r.title || '').replace(/"/g, '""')}","${r.url}","${(r.categories || []).join('; ')}","${r.article_type || ''}"`);
    });
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `shared_urls_comparison_${Date.now()}.csv`;
    a.click();
    showToast && showToast(`Exported ${comparisonData.shared.length} shared URLs`, 'success');
  }, [comparisonData, showToast]);

  if (!windows || windows.length === 0) return null;

  // Build miniature grid for portal rendering
  const recentMiniWindows = windows.slice(0, 9);
  const miniatureGrid = miniatureAnchorRef?.current && recentMiniWindows.length > 0 ? ReactDOM.createPortal(
    <div data-testid="miniature-window-grid" style={{ marginBottom: 10 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0 6px',
        borderBottom: '1px solid rgba(253, 224, 71,0.12)', marginBottom: 8
      }}>
        <span style={{ color: '#047857', fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
          <i className="fas fa-th" style={{ marginRight: 4, fontSize: '0.5rem' }} />
          Recent Collections ({recentMiniWindows.length})
        </span>
        <span style={{ color: '#52525b', fontSize: '0.48rem' }}>
          Fully interactive miniatures
        </span>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 6,
      }}>
        {recentMiniWindows.map(win => {
          const hasManualFilter = !!windowFilteredResults[win.id];
          const fr = hasManualFilter
            ? windowFilteredResults[win.id]
            : (autoUpdateEnabled ? applyFiltersGlobal(win.results) : win.results);
          const isFilt = hasManualFilter || (autoUpdateEnabled && fr.length !== (win.results || []).length);

          const MINI_SCALE = 0.52;
          const MINI_W = 380;
          const MINI_H = 440;

          return (
            <div
              key={`mini-${win.id}`}
              data-testid={`mini-window-${win.id}`}
              style={{
                width: MINI_W * MINI_SCALE,
                height: MINI_H * MINI_SCALE,
                overflow: 'hidden',
                borderRadius: 6,
                border: '1px solid rgba(253, 224, 71,0.2)',
                background: 'rgba(253, 251, 247,0.7)',
                position: 'relative',
              }}
            >
              <div style={{
                transform: `scale(${MINI_SCALE})`,
                transformOrigin: 'top left',
                width: MINI_W,
                height: MINI_H,
                overflow: 'hidden',
              }}>
                <SearchResultWindow
                  id={win.id} title={win.title} searchType={win.searchType}
                  results={win.results} filteredResults={fr} isFiltered={isFilt}
                  maxResults={localMax} onClose={onCloseWindow} onResultClick={onResultClick}
                  token={token} showToast={showToast} accentColor={win.accentColor || '#FDE047'}
                  onApplyFilters={applyFiltersToWindow} onRevertFilters={revertToSnapshot}
                  onRestoreSearchSettings={restoreSearchSettings}
                  filterSnapshot={windowSnapshots[win.id]}
                  plotOnMap={plottedWindowIds.has(win.id)} onTogglePlotOnMap={togglePlotOnMap}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>,
    miniatureAnchorRef.current
  ) : null;

  return (
    <>
      {miniatureGrid}
    <div data-testid="search-window-manager">
      {/* Control Bar */}
      <div style={{
        padding: '4px 10px', marginBottom: 6, color: '#71717a', fontSize: '0.55rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6
      }}>
        <span>URL Collection Windows ({windows.length}/60) &middot; Each window holds results from a single search.
          {autoUpdateEnabled && <span style={{ color: '#10b981', marginLeft: 5, fontWeight: 700 }}> LIVE FILTERING ON</span>}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Max URLs per window control */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} data-testid="max-urls-control">
            <span style={{ color: '#71717a', fontSize: '0.55rem', whiteSpace: 'nowrap' }}>Max URLs:</span>
            <input type="number" value={localMax} min={50} max={2000} step={50}
              onChange={e => {
                const val = Math.max(50, Math.min(2000, parseInt(e.target.value) || 600));
                setLocalMax(val);
              }}
              onBlur={() => { onChangeMaxUrls && onChangeMaxUrls(localMax); }}
              onKeyDown={e => { if (e.key === 'Enter') { onChangeMaxUrls && onChangeMaxUrls(localMax); e.target.blur(); } }}
              style={{ width: 60, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(253, 224, 71,0.25)', borderRadius: 5, color: '#334155', padding: '2px 5px', fontSize: '0.58rem', textAlign: 'center' }}
              data-testid="max-urls-input"
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: autoUpdateEnabled ? '#10b981' : '#71717a', fontSize: '0.6rem', fontWeight: 600 }}
            data-testid="auto-update-toggle">
            <input type="checkbox" checked={autoUpdateEnabled} onChange={(e) => setAutoUpdateEnabled(e.target.checked)}
              style={{ accentColor: '#10b981', width: 12, height: 12 }} />
            Auto-Update
          </label>
          {windows.length >= 2 && (
            <button onClick={() => { setCompareMode(prev => !prev); if (compareMode) setCompareWindowIds([]); }}
              style={{
                background: compareMode ? 'rgba(245, 158, 11,0.2)' : 'rgba(245, 158, 11,0.08)',
                border: `1px solid ${compareMode ? 'rgba(245, 158, 11,0.5)' : 'rgba(245, 158, 11,0.2)'}`,
                color: compareMode ? '#60a5fa' : '#93c5fd', padding: '3px 8px', borderRadius: 6,
                cursor: 'pointer', fontSize: '0.58rem', fontWeight: 700, transition: 'all 0.15s'
              }}
              data-testid="compare-mode-btn">
              <i className="fas fa-columns" style={{ marginRight: 4, fontSize: '0.5rem' }} />
              {compareMode ? 'Exit Compare' : 'Compare'}
            </button>
          )}
          <span style={{ color: '#555', fontSize: '0.45rem' }}>
            {compareMode ? `Select 2 windows to compare (${compareWindowIds.length}/2)` : autoUpdateEnabled ? 'Filters apply live to all windows' : 'Use Apply button per window to filter'}
          </span>
        </div>
      </div>

      {/* Comparison View */}
      {compareMode && comparisonData && (
        <ComparisonView
          data={comparisonData}
          onExport={exportSharedUrls}
          onClose={() => { setCompareMode(false); setCompareWindowIds([]); }}
          onResultClick={onResultClick}
          token={token}
          showToast={showToast}
        />
      )}

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
        gap: 8, marginBottom: 12
      }}>
        {windows.map(win => {
          const hasManualFilter = !!windowFilteredResults[win.id];
          const filteredResults = hasManualFilter
            ? windowFilteredResults[win.id]
            : (autoUpdateEnabled ? applyFiltersGlobal(win.results) : win.results);
          const isFiltered = hasManualFilter || (autoUpdateEnabled && filteredResults.length !== (win.results || []).length);
          const isCompareSelected = compareMode && compareWindowIds.includes(win.id);
          
          return (
            <div key={win.id} style={{ position: 'relative' }}>
              {/* Compare selection overlay */}
              {compareMode && (
                <div
                  onClick={() => toggleCompareWindow(win.id)}
                  style={{
                    position: 'absolute', top: 0, right: 0, zIndex: 10,
                    background: isCompareSelected ? 'rgba(245, 158, 11,0.3)' : 'rgba(245, 158, 11,0.1)',
                    border: `2px solid ${isCompareSelected ? '#3b82f6' : 'rgba(245, 158, 11,0.3)'}`,
                    borderRadius: '0 10px 0 8px', padding: '4px 10px', cursor: 'pointer',
                    fontSize: '0.6rem', fontWeight: 700, color: isCompareSelected ? '#60a5fa' : '#93c5fd',
                    transition: 'all 0.15s'
                  }}
                  data-testid={`compare-select-${win.id}`}
                >
                  {isCompareSelected ? (
                    <><i className="fas fa-check-square" style={{ marginRight: 4 }} />Selected</>
                  ) : (
                    <><i className="far fa-square" style={{ marginRight: 4 }} />Select</>
                  )}
                </div>
              )}
              <SearchResultWindow
                id={win.id} title={win.title} searchType={win.searchType}
                results={win.results}
                filteredResults={filteredResults}
                isFiltered={isFiltered}
                maxResults={localMax}
                onClose={onCloseWindow} onResultClick={onResultClick}
                token={token} showToast={showToast} accentColor={win.accentColor || '#FDE047'}
                onApplyFilters={applyFiltersToWindow}
                onRevertFilters={revertToSnapshot}
                onRestoreSearchSettings={restoreSearchSettings}
                filterSnapshot={windowSnapshots[win.id]}
                plotOnMap={plottedWindowIds.has(win.id)}
                onTogglePlotOnMap={togglePlotOnMap}
              />
            </div>
          );
        })}
      </div>
    </div>
    </>
  );
};

export { SearchResultWindow, SearchWindowManager };
export default SearchWindowManager;
